import {
  ToolbarGlassSurface,
  useToolbarGridMetrics,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import type { TabBarControls } from "@-/dev.improve.haniverse.components.ui.floating-tab-bar";
import { Pressable, Text, View } from "@-/com.npmjs.react-native";
import { cn } from "@-/com.npmjs.cn";
import { useCallback, useMemo } from "react";

function cardPanelTabFallbackIconName(routeName: string) {
  if (routeName === "mnemonics") {
    return "MdLightbulbOutline" as const;
  }
  return "LuBookOpen" as const;
}

export function ViewToggle({
  descriptors,
  isPanelSwitchingEnabled = true,
  navigateToTab,
  state,
  textGlass = false,
}: TabBarControls & {
  grapheme: string;
  graphemeRoutePrefix: string;
  isPanelSwitchingEnabled?: boolean;
  textGlass?: boolean;
}) {
  const { diameter } = useToolbarGridMetrics();
  const routeList = useMemo(
    () =>
      state.routes.filter(
        (route: { name: string }) =>
          // `index` is the bare Reference route sibling of `info`/`mnemonics`. Expo Router still
          // registers it on this MaterialTopTabs navigator, but Reference is a separate layout
          // branch — never a third panel toggle (mobile stays info/mnemonics only).
          !["index", "_sitemap", "+not-found"].includes(route.name),
      ),
    [state.routes],
  );

  const focusedRouteKey = state.routes[state.index]?.key;

  const handlePress = useCallback(
    (routeName: string, isFocused: boolean) => {
      if (isFocused || !isPanelSwitchingEnabled) {
        return;
      }
      const route = state.routes.find((entry) => entry.name === routeName);
      if (route) {
        navigateToTab(route.key);
      }
    },
    [isPanelSwitchingEnabled, navigateToTab, state.routes],
  );

  if (textGlass) {
    return (
      <ToolbarGlassSurface
        className="flex-row items-center bg-background p-0.5"
        interactive={false}
        style={{ height: diameter }}
      >
        {routeList.map((route: { key: string; name: string }) => {
          const selected = route.key === focusedRouteKey;
          const label = descriptors[route.key].options.title ?? route.name;
          return (
            <Pressable
              accessibilityLabel={label}
              accessibilityRole="tab"
              accessibilityState={{ disabled: !isPanelSwitchingEnabled, selected }}
              className="h-full"
              disabled={!isPanelSwitchingEnabled}
              key={route.key}
              onPress={() => handlePress(route.name, selected)}
              testID={
                route.name === "info" ? "morpheme-panel-tab-info" : "morpheme-panel-tab-mnemonics"
              }
            >
              {selected ? (
                <ToolbarGlassSurface className="h-full items-center justify-center px-4" selected>
                  <Text className="text-sm font-semibold text-foreground">{label}</Text>
                </ToolbarGlassSurface>
              ) : (
                <View className="h-full items-center justify-center px-4">
                  <Text className="text-sm font-semibold text-muted-foreground">{label}</Text>
                </View>
              )}
            </Pressable>
          );
        })}
      </ToolbarGlassSurface>
    );
  }

  return (
    <View
      className="flex-row items-center rounded-full bg-muted/40 p-0.5"
      style={{ height: diameter }}
    >
      {routeList.map((route: { key: string; name: string }) => {
        const descriptor = descriptors[route.key];
        const { options } = descriptor;
        const isFocused = route.key === focusedRouteKey;
        // The icon's color is driven by its theme-aware `className`
        // (`text-foreground` / `text-muted-foreground/60`), so the `color` here is only the
        // React Navigation arg shape and is intentionally not forwarded to the icon — passing a
        // hardcoded hex made the focused icon black-on-dark (invisible) in dark mode.
        const tabBarIcon = options.tabBarIcon?.({
          color: isFocused ? "#000000" : "#888888",
          focused: isFocused,
        });

        return (
          <Pressable
            accessibilityLabel={route.name === "info" ? "Info panel" : "Mnemonics panel"}
            accessibilityRole="button"
            accessibilityState={{ disabled: !isPanelSwitchingEnabled, selected: isFocused }}
            className={cn(
              "h-full flex-row items-center justify-center rounded-full",
              isFocused && "bg-background shadow-sm",
            )}
            disabled={!isPanelSwitchingEnabled}
            key={route.key}
            onPress={() => handlePress(route.name, isFocused)}
            // The review card nests this control inside PagerView. Its pan recognizer can cancel
            // Pressable's deferred `onPress` even for a stationary accessibility tap, leaving the
            // visible toggle inert. Commit on press-in before the parent recognizer arbitrates.
            onPressIn={() => handlePress(route.name, isFocused)}
            style={{ width: diameter - 4 }}
            testID={
              route.name === "info" ? "morpheme-panel-tab-info" : "morpheme-panel-tab-mnemonics"
            }
          >
            {tabBarIcon ?? (
              <Icon
                className={isFocused ? "text-foreground" : "text-muted-foreground/60"}
                name={cardPanelTabFallbackIconName(route.name)}
                size={route.name === "mnemonics" ? 15 : 14}
              />
            )}
          </Pressable>
        );
      })}
    </View>
  );
}
